import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Package, Download, Sparkles, Loader2, Coins } from "lucide-react";
import { toast } from "sonner";
import {
  studentStudyPackPreview,
  studentStudyPackCheckout,
  openStudyPackPdf,
} from "@/lib/studyPack";
import { getMyCredits } from "@/lib/referrals";

function formatMoney(amount, ccy) {
  const sym = ccy === "INR" ? "₹" : "$";
  const n = Number(amount || 0);
  return `${sym}${n.toLocaleString(undefined, {
    maximumFractionDigits: 2,
    minimumFractionDigits: ccy === "USD" ? 2 : 0,
  })}`;
}

/**
 * Compact upsell card shown above the chapter tabs on the student course view.
 *
 * Hidden when the course's Study Pack is disabled.
 */
export default function StudyPackUpsellCard({ enrollmentId, courseId }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applyCredits, setApplyCredits] = useState(false);
  const [credits, setCredits] = useState(null);

  const refresh = () => {
    if (!enrollmentId) return;
    studentStudyPackPreview(enrollmentId)
      .then(setPreview)
      .catch(() => setPreview(null));
    getMyCredits()
      .then(setCredits)
      .catch(() => setCredits(null));
  };

  useEffect(refresh, [enrollmentId]);

  if (!preview || !preview.enabled) return null;

  const totalInr = Number(credits?.totals?.total_active_inr || 0);
  const totalUsd = Number(credits?.totals?.total_active_usd || 0);

  const onBuy = async (currency) => {
    setBusy(true);
    try {
      const r = await studentStudyPackCheckout(enrollmentId, {
        currency,
        apply_credits: applyCredits,
      });
      if ((r.provider === "free" || r.free) && r.status === "succeeded") {
        const applied = Number(r.credits_applied || 0);
        toast.success(
          applied > 0
            ? `🎉 Unlocked with ${currency === "INR" ? "₹" : "$"}${applied} in credit`
            : "Unlocked — your Study Pack is ready"
        );
        refresh();
        return;
      }
      if (r.checkout_url) {
        window.location.href = r.checkout_url;
        return;
      }
      toast.message("Razorpay order created", {
        description: `Order ${r.order_id || ""} — complete payment in the modal.`,
      });
    } catch (err) {
      const d = err?.response?.data?.detail || "Could not start checkout";
      toast.error(typeof d === "string" ? d : "Could not start checkout");
    } finally {
      setBusy(false);
    }
  };

  const onDownload = async () => {
    setBusy(true);
    try {
      await openStudyPackPdf(courseId);
    } catch (err) {
      toast.error(err?.message || "Failed to open PDF");
    } finally {
      setBusy(false);
    }
  };

  if (preview.can_download) {
    return (
      <Card className="border-emerald-200 bg-emerald-50/40" data-testid="study-pack-owned-card">
        <CardContent className="py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <Package className="h-5 w-5 text-emerald-700 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-emerald-900">
                Study Pack unlocked
              </div>
              <div className="text-xs text-emerald-800">
                {preview.is_free
                  ? "Your teacher is sharing this Study Pack for free."
                  : "Thanks — your purchase is on record."}{" "}
                One PDF with every shared chapter's reference book.
              </div>
            </div>
          </div>
          <Button
            onClick={onDownload}
            disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="study-pack-download-btn"
          >
            {busy ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-1 h-4 w-4" />
            )}
            Download Study Pack PDF
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-amber-200 bg-amber-50/40" data-testid="study-pack-upsell-card">
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-amber-700 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-amber-900">
                Study Pack — one PDF, every chapter
              </div>
              <div className="text-xs text-amber-800" data-testid="study-pack-price">
                {preview.is_free
                  ? "Free for enrolled students."
                  : `Just ${formatMoney(preview.price_inr, "INR")} or ${formatMoney(
                      preview.price_usd,
                      "USD"
                    )} — bundled, watermarked, yours forever.`}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {preview.is_free ? (
              <Button
                onClick={() => onBuy("INR")}
                disabled={busy}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="study-pack-claim-free"
              >
                {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                Claim free
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => onBuy("INR")}
                  disabled={busy}
                  className="border-amber-300 text-amber-800"
                  data-testid="study-pack-buy-inr"
                >
                  {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                  Buy {formatMoney(preview.price_inr, "INR")} (UPI)
                </Button>
                <Button
                  onClick={() => onBuy("USD")}
                  disabled={busy}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="study-pack-buy-usd"
                >
                  {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                  Buy {formatMoney(preview.price_usd, "USD")}
                </Button>
              </>
            )}
          </div>
        </div>

        {!preview.is_free && (totalInr > 0 || totalUsd > 0) && (
          <div
            className="rounded-md border border-amber-200 bg-white/60 px-3 py-2 flex items-start gap-2"
            data-testid="study-pack-apply-credits-row"
          >
            <Checkbox
              id="sp-apply-credits"
              checked={applyCredits}
              onCheckedChange={(v) => setApplyCredits(!!v)}
              data-testid="study-pack-apply-credits-toggle"
              className="mt-0.5"
            />
            <label
              htmlFor="sp-apply-credits"
              className="text-xs text-amber-900 cursor-pointer flex items-center gap-1 leading-relaxed"
            >
              <Coins className="h-3 w-3" />
              Apply my credits (
              {totalInr > 0 && `₹${totalInr.toLocaleString()}`}
              {totalInr > 0 && totalUsd > 0 && " · "}
              {totalUsd > 0 && `$${totalUsd.toFixed(2)}`} available)
            </label>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
